(ns frontend.ui.component.lp-portal.voima-components.pdf.card.view
  (:require
   [frontend.ui.component.lp-portal.page.pdf.view :as page]
   [frontend.ui.style.theme.core :as theme]
   [clojure.string :as string]
   [frontend.tenant-customizations :as tenant-customizations]))

(defn- format-title
  [title preserve-title-case?]
  (if preserve-title-case?
    (str title)
    (string/upper-case (str title))))

(defn- card-raw
  [title-or-props unbreakable? & children]
  (let [{:keys [title title-color preserve-title-case?]
         :or {preserve-title-case? false}}
        (if (map? title-or-props)
          title-or-props
          {:title title-or-props})
        title-definition (when-not (nil? title)
                           (merge {:text   (format-title title preserve-title-case?)
                                   :bold   true
                                   :margin [0 0 0 0]
                                   :color  (or title-color
                                               (theme/color [:pdf :heading]))}
                                  (when (= (theme/get-theme) :theme.lp-portal/lifeline)
                                    {:font "SuperiorTitleBlack"
                                     :fontSize 11
                                     :bold false
                                     :color (or title-color "#000000")})
                                  (when (= (theme/get-theme) :theme.lp-portal/capman)
                                    {:fontSize 13
                                     :text (str title)
                                     :bold true})))]
    (merge {:stack (->> children
                        (cons title-definition)
                        (remove nil?)
                        (vec))}
           (when unbreakable?
             {:unbreakable true}))))

(defn- no-borders-layout
  []
  {:hLineWidth   (fn [_ _] 0)
   :vLineWidth   (fn [_ _] 0)
   :paddingLeft  (fn [_ _] 0)
   :paddingRight (fn [_ _] 0)
   :paddingTop   (fn [_ _] 0)
   :paddingBottom (fn [_ _] 0)})

(defn- card-raw-with-background
  [title-or-props unbreakable? & children]
  (let [{:keys [title preserve-title-case?]
         :or {preserve-title-case? false}}
        (if (map? title-or-props)
          title-or-props
          {:title title-or-props})
        capman-theme? (= (theme/get-theme) :theme.lp-portal/capman)
        title-node
        (when-not (nil? title)
          (merge {:text   (format-title title preserve-title-case?)
                  :bold   true
                  :margin [0 0 6 4]               ;; space between title and card
                  :color  (theme/color [:pdf :heading])}
                 (when (= (theme/get-theme) :theme.lp-portal/lifeline)
                   {:font "SuperiorTitleBlack"
                    :fontSize 11
                    :bold false
                    :color "#000000"})
                 (when capman-theme?
                   {:fontSize 13
                    :text (str title)
                    :bold true})))

        content-stack (->> children
                           (remove nil?)
                           vec)

        card-cell {:border    [false false false false]
                   :fillColor "#F0EEEA"
                   :margin    (if capman-theme? 
                                [7 7 7 7]
                                [10 10 10 10])
                   :stack     content-stack}

        card-table {:table  {:widths ["*"]
                             :body   [[card-cell]]}
                    :layout (no-borders-layout)}]

    (merge
     {:stack (cond-> []
               title-node (conj title-node)
               true       (conj card-table))}
     (when unbreakable?
       {:unbreakable true}))))

(defn card-no-breaks-must-fit-on-1-page
  [title & children]
  (apply card-raw (concat [title true] children)))

(defn card
  [title & children]
  (apply card-raw (concat [title false] children)))

(defn table-card
  [title & children]
  (merge (apply card-raw
                (concat [title false]
                        children))
         {:margin [0 0 0 (if (empty? (remove nil? children)) 0 30)]}))

(defn custom-margins-card
  [title margins & children]
  (merge (apply card-raw
                (concat [title false]
                        children))
         {:margin margins}))

(defn custom-margins-card-with-background
  [title margins & children]
  (merge (apply card-raw-with-background
                (concat [title true]
                        children))
         {:margin margins}))

(defn first-row-card
  [title & children]
  (let [margin-x 20
        margin-y 20]
    (merge (apply card-raw
                  (concat [title false]
                          children))
           {:absolutePosition {:x margin-x :y (+ page/default-margin-y margin-y)}})))

(defn second-row-card
  [title & children]
  (let [margin-x 20
        margin-y 20]
    (merge (apply card-raw
                  (concat [title false]
                          children))
           {:absolutePosition {:x margin-x :y (+ (/ page/page-height 2) margin-y)}})))

(defn one-third-row-card
  [title & children]
  (let [margin-y 20]
    (merge (apply card-raw
                  (concat [title false]
                          children))
           {:absolutePosition {:y (+ (/ page/page-height 3) margin-y)}})))

(defn one-fourth-row-card
  [title & children]
  (let [margin-x 20
        margin-y 20]
    (merge (apply card-raw
                  (concat [title false]
                          children))
           {:absolutePosition {:x margin-x :y (+ (/ page/page-height 4) margin-y)}})))

(defn bottom-page-card
  [title & children]
  (let [margin-x 20]
    (merge (apply card-raw
                  (concat [title false]
                          children))
           {:absolutePosition {:x margin-x :y (- page/page-height (/ page/page-height 4))}})))

(defn first-row-first-column-card
  [title border? & children]
  (let [margin-x (if (tenant-customizations/capman?)
                   20
                   (+ page/default-margin-x 20))
        margin-y 20]
    (merge (apply card-raw
                  (concat [title false]
                          children
                          (when border? (:stack (page/chart-background-dynamic-position 0 36 (- (/ page/page-height 2) 36))))))
           {:absolutePosition {:x margin-x :y (+ page/default-margin-y margin-y)}})))

(defn first-row-second-column-card
  [title border? & children]
  (let [margin-x (if (tenant-customizations/capman?)
                   20
                   (+ page/default-margin-x 20))
        margin-y 20]
    (merge (apply card-raw
                  (concat [title false]
                          children
                          (when border?
                            (:stack (page/chart-background-dynamic-position page/page-width-half 36 (- (/ page/page-height 2) 36))))))
           {:absolutePosition {:x (+ page/page-width-half margin-x) :y (+ page/default-margin-y margin-y)}})))

(defn second-row-first-column-card
  [title border? & children]
  (let [margin-x (if (tenant-customizations/capman?)
                   20
                   (+ page/default-margin-x 20))
        margin-y 20]
    (merge (apply card-raw
                  (concat [title false]
                          children
                          (when border?
                            (:stack (page/chart-background-dynamic-position 0 (/ page/page-height 2) (- (/ page/page-height 2) 34))))))
           {:absolutePosition {:x margin-x :y (+ (/ page/page-height 2) margin-y)}})))

(defn second-row-second-column-card
  [title border? & children]
  (let [margin-x (if (tenant-customizations/capman?)
                   20
                   (+ page/default-margin-x 20))
        margin-y 20]
    (merge (apply card-raw
                  (concat [title false]
                          children
                          (when border?
                            (:stack (page/chart-background-dynamic-position page/page-width-half
                                                                            (/ page/page-height 2)
                                                                            (/ (- page/page-height (* 2 34)) 2))))))
           {:absolutePosition {:x (+ page/page-width-half margin-x) :y (+ (/ page/page-height 2) margin-y)}})))

(defn- two-column-position [col-idx]
  (let [margin-x 20
        column-width (/ page/page-width 2)]
    (+ margin-x (* col-idx column-width))))

(defn- three-row-position [row-idx]
  (let [margin-y 20
        row-height (/ page/page-height 3)]
    (+ margin-y (* row-idx row-height))))

(defn grid-card
  [row-idx col-idx title & children]
  (merge (apply card-raw (concat [title false]
                                 children))
         {:absolutePosition {:x (two-column-position col-idx)
                             :y (three-row-position row-idx)}}))
